import { useState } from 'react';

import type {
  Address,
  EndorsementAddress,
  EndorsementChangeInput,
  EndorsementChangeValue,
  EndorsementDetail,
  PolicyDetail,
  PolicyParty,
} from '@/shared/api/resources';
import { formatDocument } from '@/shared/lib/format';
import { AddressFields } from '@/shared/ui/AddressFields';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Field } from '@/shared/ui/Field';
import { Modal } from '@/shared/ui/Modal';

import { enderecoDaFotografia, enderecoEmTexto, textoDaParte } from './endosso';

/**
 * Passo "Informacoes do endosso" do endosso neutro (tela `/chunk-403dd03d` do OnPoint;
 * END-034 a END-047).
 *
 * Mostra o que a apolice diz do tomador e do segurado (a fotografia da emissao) e
 * deixa trocar a razao social, o nome social, o nome fantasia e o endereco. O antes
 * de cada alteracao e o da apolice, gravado pelo servidor; aqui so se escolhe o
 * depois. "Proximo" grava a lista inteira de alteracoes no rascunho.
 */

type Escopo = 'tomador' | 'segurado';
type CampoDeTexto = 'legal_name' | 'social_name' | 'trade_name';
type Chave = `${Escopo}.${CampoDeTexto | 'address'}`;

const titulosDosDialogos: Record<CampoDeTexto, { titulo: string; rotulo: string }> = {
  legal_name: { titulo: 'Razao social', rotulo: 'Razao social' },
  social_name: { titulo: 'Nome Social', rotulo: 'Nome social' },
  trade_name: { titulo: 'Nome Fantasia', rotulo: 'Nome fantasia' },
};

/** As alteracoes do rascunho, pela chave escopo.campo. */
function alteracoesIniciais(
  endosso: EndorsementDetail,
): Map<Chave, EndorsementChangeValue> {
  const mapa = new Map<Chave, EndorsementChangeValue>();
  for (const alteracao of endosso.changes ?? []) {
    if (alteracao.scope === 'apolice') continue;
    mapa.set(`${alteracao.scope}.${alteracao.field}` as Chave, alteracao.after);
  }
  return mapa;
}

function paraEndereco(endereco: EndorsementAddress): Address {
  return {
    zip_code: endereco.zip_code,
    street: endereco.street,
    number: endereco.number,
    complement: endereco.complement,
    district: endereco.district,
    city: endereco.city,
    state: endereco.state,
  };
}

function deEndereco(endereco: Address): EndorsementAddress {
  return {
    zip_code: endereco.zip_code.replace(/\D/g, ''),
    street: endereco.street,
    number: endereco.number,
    complement: endereco.complement ?? '',
    district: endereco.district,
    city: endereco.city,
    state: endereco.state,
  };
}

export function NeutralEndorsementStep({
  policy,
  endorsement,
  editavel,
  salvando,
  onProximo,
}: {
  policy: PolicyDetail;
  endorsement: EndorsementDetail;
  editavel: boolean;
  salvando: boolean;
  onProximo: (changes: EndorsementChangeInput[] | null) => void;
}) {
  const [alteracoes, setAlteracoes] = useState(() => alteracoesIniciais(endorsement));
  const [editando, setEditando] = useState<Chave | null>(null);
  const [mudou, setMudou] = useState(false);

  function grava(chave: Chave, valor: EndorsementChangeValue | null) {
    setAlteracoes((anteriores) => {
      const proximas = new Map(anteriores);
      if (valor === null) proximas.delete(chave);
      else proximas.set(chave, valor);
      return proximas;
    });
    setMudou(true);
    setEditando(null);
  }

  function lista(): EndorsementChangeInput[] {
    const saida: EndorsementChangeInput[] = [];
    for (const [chave, valor] of alteracoes) {
      const [escopo, campo] = chave.split('.') as [Escopo, CampoDeTexto | 'address'];
      saida.push({ scope: escopo, field: campo, after: valor });
    }
    return saida;
  }

  const tomador = policy.parties?.policyholder ?? null;
  const segurado = policy.parties?.insured ?? null;

  return (
    <div className="flex flex-col gap-4">
      <Alert tone="info" title="O Endosso Neutro esta de cara nova!">
        Ajustes sem movimentacao no premio: troque a razao social, o nome social, o nome
        fantasia ou o endereco do tomador e do segurado. A apolice so muda quando o
        endosso for emitido.
      </Alert>

      <SecaoDaParte
        titulo="Dados do tomador"
        escopo="tomador"
        rotuloDoDocumento="CNPJ do tomador"
        parte={tomador}
        alteracoes={alteracoes}
        editavel={editavel}
        comNomeFantasia
        onEditar={setEditando}
        onDesfazer={(chave) => grava(chave, null)}
      />

      {segurado ? (
        <SecaoDaParte
          titulo="Dados do segurado"
          escopo="segurado"
          rotuloDoDocumento="CNPJ do segurado"
          parte={segurado}
          alteracoes={alteracoes}
          editavel={editavel}
          comNomeFantasia
          onEditar={setEditando}
          onDesfazer={(chave) => grava(chave, null)}
        />
      ) : (
        <Alert tone="info" title="Dados do segurado">
          A apolice nao tem segurado na fotografia da emissao.
        </Alert>
      )}

      {editavel ? (
        <div className="flex justify-end">
          <Button
            loading={salvando}
            disabled={alteracoes.size === 0}
            onClick={() => onProximo(mudou ? lista() : null)}
          >
            Proximo
          </Button>
        </div>
      ) : null}

      {editando ? (
        <DialogoDeEdicao
          chave={editando}
          parte={editando.startsWith('tomador') ? tomador : segurado}
          valorAtual={alteracoes.get(editando)}
          onClose={() => setEditando(null)}
          onSalvar={(valor) => grava(editando, valor)}
        />
      ) : null}
    </div>
  );
}

function SecaoDaParte({
  titulo,
  escopo,
  rotuloDoDocumento,
  parte,
  alteracoes,
  editavel,
  comNomeFantasia,
  onEditar,
  onDesfazer,
}: {
  titulo: string;
  escopo: Escopo;
  rotuloDoDocumento: string;
  parte: PolicyParty | null;
  alteracoes: Map<Chave, EndorsementChangeValue>;
  editavel: boolean;
  comNomeFantasia: boolean;
  onEditar: (chave: Chave) => void;
  onDesfazer: (chave: Chave) => void;
}) {
  const nome = escopo === 'tomador' ? 'do tomador' : 'do segurado';
  const linhas: { campo: CampoDeTexto | 'address'; rotulo: string; acao: string }[] = [
    {
      campo: 'legal_name',
      rotulo: `Razao social ${nome}`,
      acao: `Editar razao social ${nome}`,
    },
    {
      campo: 'social_name',
      rotulo: `Nome social ${nome}`,
      acao: textoDaParte(parte, 'social_name')
        ? `Editar nome social ${nome}`
        : 'Adicionar nome social',
    },
    ...(comNomeFantasia
      ? [
          {
            campo: 'trade_name' as const,
            rotulo: `Nome fantasia ${nome}`,
            acao: textoDaParte(parte, 'trade_name')
              ? `Editar nome fantasia ${nome}`
              : 'Adicionar nome fantasia',
          },
        ]
      : []),
    { campo: 'address', rotulo: `Endereco ${nome}`, acao: `Editar endereco ${nome}` },
  ];

  return (
    <Card>
      <CardHeader title={titulo} />
      <CardBody className="flex flex-col gap-3">
        <div>
          <p className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
            {rotuloDoDocumento}
          </p>
          <p className="mt-0.5 text-sm text-ink-900">
            {parte?.document ? formatDocument(parte.document) : '--'}
          </p>
        </div>
        {linhas.map((linha) => {
          const chave: Chave = `${escopo}.${linha.campo}`;
          const novo = alteracoes.get(chave);
          const atual =
            linha.campo === 'address'
              ? enderecoEmTexto(enderecoDaFotografia(parte))
              : textoDaParte(parte, linha.campo);
          const depois =
            novo === undefined
              ? null
              : linha.campo === 'address'
                ? enderecoEmTexto(novo.address)
                : (novo.text ?? '');
          return (
            <div
              key={linha.campo}
              className="flex flex-wrap items-start justify-between gap-3 border-t border-line pt-3"
            >
              <div className="min-w-0">
                <p className="text-2xs font-semibold uppercase tracking-wide text-ink-500">
                  {linha.rotulo}
                </p>
                <p
                  className={
                    depois !== null
                      ? 'text-sm text-ink-500 line-through'
                      : 'text-sm text-ink-900'
                  }
                >
                  {atual || 'Nao informado'}
                </p>
                {depois !== null ? (
                  <p className="text-sm font-medium text-brand-700">
                    {depois || 'Em branco'}
                  </p>
                ) : null}
              </div>
              {editavel ? (
                <div className="flex gap-2">
                  {depois !== null ? (
                    <Button size="sm" variant="ghost" onClick={() => onDesfazer(chave)}>
                      Desfazer
                    </Button>
                  ) : null}
                  <Button size="sm" variant="secondary" onClick={() => onEditar(chave)}>
                    {linha.acao}
                  </Button>
                </div>
              ) : null}
            </div>
          );
        })}
      </CardBody>
    </Card>
  );
}

function DialogoDeEdicao({
  chave,
  parte,
  valorAtual,
  onClose,
  onSalvar,
}: {
  chave: Chave;
  parte: PolicyParty | null;
  valorAtual: EndorsementChangeValue | undefined;
  onClose: () => void;
  onSalvar: (valor: EndorsementChangeValue) => void;
}) {
  const campo = chave.split('.')[1] as CampoDeTexto | 'address';
  const [texto, setTexto] = useState(
    campo === 'address' ? '' : (valorAtual?.text ?? textoDaParte(parte, campo)),
  );
  const [endereco, setEndereco] = useState<Address>(() =>
    paraEndereco(valorAtual?.address ?? enderecoDaFotografia(parte)),
  );

  if (campo === 'address') {
    const incompleto = endereco.street.trim() === '' || endereco.city.trim() === '';
    return (
      <Modal
        open
        onClose={onClose}
        title="Endereco"
        description="O endereco novo da parte na apolice. Rua e cidade sao obrigatorias."
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              disabled={incompleto}
              onClick={() => onSalvar({ address: deEndereco(endereco) })}
            >
              Salvar
            </Button>
          </>
        }
      >
        <AddressFields value={endereco} onChange={setEndereco} legend="Endereco" />
      </Modal>
    );
  }

  const { titulo, rotulo } = titulosDosDialogos[campo];
  return (
    <Modal
      open
      onClose={onClose}
      title={titulo}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            disabled={campo === 'legal_name' && texto.trim() === ''}
            onClick={() => onSalvar({ text: texto.trim() })}
          >
            Salvar
          </Button>
        </>
      }
    >
      <Field
        label={rotulo}
        placeholder={`Digite o ${rotulo.toLowerCase()}`}
        value={texto}
        maxLength={200}
        onChange={(event) => setTexto(event.target.value)}
      />
    </Modal>
  );
}
